import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { FL } from "@/constants/testIds/fairylights";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Wedding",
  "Engagement",
  "Corporate",
  "Private Party",
  "Custom Décor",
  "Other",
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "Wedding",
    event_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("A few fields are still missing.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        event_date: form.event_date || null,
      };
      const { data } = await axios.post(`${API}/contact`, payload);
      if (data.status === "ok") {
        toast.success("Enquiry sent. We'll reply within one working day.");
        setSubmitted(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("We couldn't send your enquiry. Please try again or email us.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="contact"
      data-testid={FL.contactSection}
      className="relative bg-[#4A0E1F] px-6 py-24 sm:px-10 md:px-14 md:py-32"
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 md:grid-cols-[1fr_1.1fr] md:gap-24">
        {/* Left column — headline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
              Chapter VI · Begin
            </span>
          </div>
          <h3 className="font-serif text-[10vw] leading-[0.95] tracking-tight text-[#FDFBF7] sm:text-6xl md:text-7xl">
            Tell us
            <br />
            <span className="italic text-[#D4AF37]">about the day.</span>
          </h3>
          <p className="mt-8 max-w-md font-body text-[15px] leading-[1.75] text-[#FDFBF7]/75">
            We take on a small number of events each season. Send the shape of what
            you're planning — even a paragraph. We'll reply within one working day.
          </p>

          <div className="mt-10 space-y-2 font-body text-[13px] leading-[1.9] text-[#FDFBF7]/70">
            <div>
              <span className="text-[#D4AF37]">Factory ·</span> Al Awir, Dubai, UAE
            </div>
            <div>
              <span className="text-[#D4AF37]">Office ·</span> Central Park Tower, Dubai, UAE
            </div>
            <div>
              <span className="text-[#D4AF37]">Phone ·</span>{" "}
              <a href="tel:+971528035188" className="hover:text-[#D4AF37] transition-colors">
                +971 52 803 5188
              </a>
            </div>
            <div>
              <span className="text-[#D4AF37]">Email ·</span>{" "}
              <a href="mailto:info@fairylights.ae" className="hover:text-[#D4AF37] transition-colors">
                info@fairylights.ae
              </a>
            </div>
          </div>
        </motion.div>

        {/* Right column — form */}
        <motion.form
          data-testid={FL.contactForm}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-8"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <Field label="Name" testid={FL.contactName}>
              <input
                required
                data-testid={FL.contactName}
                className="line-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                aria-label="Name"
              />
            </Field>
            <Field label="Email" testid={FL.contactEmail}>
              <input
                required
                type="email"
                data-testid={FL.contactEmail}
                className="line-input"
                value={form.email}
                onChange={set("email")}
                placeholder="you@domain.com"
                aria-label="Email"
              />
            </Field>
            <Field label="Phone" testid={FL.contactPhone}>
              <input
                required
                type="tel"
                data-testid={FL.contactPhone}
                className="line-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+971"
                aria-label="Phone"
              />
            </Field>
            <Field label="Event type" testid={FL.contactEventType}>
              <select
                data-testid={FL.contactEventType}
                className="line-input appearance-none pr-6"
                value={form.event_type}
                onChange={set("event_type")}
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23D4AF37' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 4px center",
                }}
                aria-label="Event type"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#1A050B] text-[#FDFBF7]">
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Event date (optional)" testid={FL.contactEventDate}>
            <input
              type="date"
              data-testid={FL.contactEventDate}
              className="line-input"
              value={form.event_date}
              onChange={set("event_date")}
              aria-label="Event date"
            />
          </Field>

          <Field label="Message" testid={FL.contactMessage}>
            <textarea
              required
              data-testid={FL.contactMessage}
              className="line-input resize-none"
              value={form.message}
              onChange={set("message")}
              placeholder="Where, when, roughly how many guests, and the feeling you're after."
              rows={4}
              aria-label="Message"
            />
          </Field>

          <div className="flex items-center justify-between pt-2">
            <span className="font-body text-[11px] uppercase tracking-[0.24em] text-[#FDFBF7]/50">
              {submitted ? "Enquiry received · thank you" : "We reply within one working day"}
            </span>
            <button
              type="submit"
              data-testid={FL.contactSubmit}
              disabled={submitting || submitted}
              className="pill pill-primary group disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitted ? "Sent" : submitting ? "Sending…" : "Send enquiry"}
              <Send className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="line-label">{label}</span>
      {children}
    </label>
  );
}
